// Tagging system types for the crypto news CMS

// Monitoring lifecycle vocabulary + normalizer — the ONE canonical source is the cross-runtime
// pure module shared/graph/monitoring-state.js (backend require + this TS re-export), mirroring
// how normalizeEntityPriority lives beside its enum. Re-exported so FE consumers can
// `import { normalizeMonitoringState } from '@/types/tags'` (parallel to normalizeEntityPriority).
import type { MonitoringState } from '@shared/graph/monitoring-state.js';
export type { MonitoringState } from '@shared/graph/monitoring-state.js';
export { normalizeMonitoringState, MONITORING_STATES } from '@shared/graph/monitoring-state.js';

export interface Tag {
  id: string;
  name: string;
  slug: string;
  description?: string;
  label?: string; // Display label for parent tags (e.g., "Blockchain Technology", "Decentralized Finance")
  parent: string | null; // Parent tag ID (null for parent tags) - LEGACY, will be deprecated after migration
  type: TagType; // PARENT or CHILD - LEGACY, will be deprecated after migration
  parentCategory?: ParentTagCategory; // Only for parent tags - LEGACY
  color?: string; // UI color coding (#3B82F6, etc.)
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;

  // Chain scope: which blockchain(s) this entity lives on. Default for legacy tags: ['abstract'].
  // Descout is multi-chain — initial scope Abstract + Base. New tags must set this explicitly.
  chains?: string[];

  // Graph-hygiene facets (backend-written, EO3). `defunct` = a closed/abandoned project (still
  // tracked, kept for history — never deleted); `excludeFromGraph` = intentionally hidden from the
  // entity graph. Both suppress contribution-gap surfacing (shared/knowledge/entity-gaps.js) and
  // knowledge-page maintenance (entity-page-ingest decideIngest). Additive/optional; absent = false.
  // NOTE: EntityBrief.tsx keeps its own defunct heuristic (not driven by this field yet) — separate.
  defunct?: boolean;
  excludeFromGraph?: boolean;

  // NEW: Entity System Fields (all optional for backward compatibility)
  entityType?: EntityType | null; // Type of entity (PERSON, PROJECT, GAME, etc.) or null for descriptive tags
  entityPriority?: EntityPriority; // Importance weighting for newsworthiness and content surfacing
  hasGlossaryPage?: boolean; // If true, shows full entity page with rich content
  glossaryContent?: string; // Full markdown content for glossary pages
  glossaryContentMeta?: {
    source?: 'ai' | 'manual' | 'hybrid' | 'owner'; // How the content was generated
    updatedAt?: string; // ISO date string of last update
    sources?: string[]; // Data sources used (e.g., 'twitter', 'website', 'firecrawl')
  };
  seoExcerpt?: string; // SEO meta description (distinct from description)

  // Entity links
  links?: EntityLink[]; // External links (website, social, docs)

  // Entity images
  images?: EntityImage[]; // Images for entity (max 5, 2MB each)

  // NEW: Direct asset linking (centralizes token/NFT relationships)
  linkedAsset?: {
    type: 'token' | 'nft' | 'collection';  // Asset type: token (coins), nft/collection (both valid for NFT collections)
    assetId: string;         // Symbol for tokens (e.g., 'BTC'), slug for collections (e.g., 'bigcoin-miners')
    chain?: string;          // Chain the asset lives on (e.g., 'abstract', 'base'). Disambiguates cross-chain assets like USDC.
  };

  // Tag associations (like asset tagging, but for tags)
  associatedTags?: string[]; // Tag IDs this tag is associated with (bidirectional)

  // Twitter integration (Build 2 & 3)
  twitter?: {
    handleId: string;           // Link to TwitterHandle document
    username: string;           // Twitter handle (for quick display)
    followers: number;          // Cached follower count
    verified: boolean;          // Verification status
    lastSynced: Date;           // Last profile sync
    autoUpdate: boolean;        // Auto-refresh profile weekly
  };
  twitterHandles?: string[]; // Array of handle IDs (e.g., ["abstractchain", "lucanetz"])
  primaryTwitterHandle?: string; // ID of the primary handle to use for profile display (must be in twitterHandles array)

  // Migration tracking
  migrationStatus?: MigrationStatus; // Track conversion from old to new system

  // Analytics & Performance
  articleCount: number;
  breakingNewsCount: number; // NEW: Track breaking news separately for granular analytics
  viewCount: number;
  trending: boolean;
  weight: number; // Relevance scoring (0-100)

  // SEO & Discovery
  fullPath: string; // e.g., "blockchain/abstract" or "collections/lil-pudgys"

  // ── Tracked-SKU additions (C9) — additive, all optional ─────────────────────

  /**
   * Subscription state written by the billing/ops layer.
   * Absent on free/unlisted entities.
   */
  subscription?: {
    tier?: string;            // e.g. 'tracked', 'featured'
    coverageState?: 'ok' | 'degraded' | 'breached';
    /** ISO date — when this subscription was last renewed / verified. */
    renewedAt?: string;
  };

  /**
   * Human-screened verified status.
   * INVARIANT: only a human screening transition may set state='verified'.
   * See src/lib/attributed-claim.ts for the full type + enforcement.
   */
  verifiedStatus?: {
    state: 'unverified' | 'screening' | 'verified' | 'denied';
    screenedBy?: string;
    /** ISO date of the last human screening event. */
    screenedAt?: string;
  };

  /**
   * Approved attributed claims ("X told us Y").
   * Populated by the morning-agent; only 'approved' items are shown on the profile page.
   */
  attributed_claims?: Array<{
    id: string;
    claim: string;
    /** ISO date string — when the claim was made/observed. */
    asOf: string;
    sources?: Array<{ url?: string; type: string }>;
    status: 'draft' | 'approved';
    approvedBy?: string;
    approvedAt?: string;
  }>;

  /**
   * Coarse economic-model facet (WS-K) — the SEED for signal coverage.
   * `entityType` alone is too blunt (DYLI is `project`, like many non-marketplaces),
   * so this names HOW the entity makes/moves money, which determines how much of its
   * real activity a secondary-volume signal actually observes (its "coverage").
   * Drives the coverage map in shared/config/economic-model-coverage.js:
   *   collection/token = HIGH coverage; marketplace/game/protocol = LOW; social = N/A.
   * `note` is EVERGREEN free-text (no volatile figures — see the spec's rendering rule).
   * See docs/superpowers/specs/2026-06-14-signal-coverage-and-open-questions-design.md §1.
   */
  economicModel?: {
    type: 'collection' | 'marketplace' | 'game' | 'protocol' | 'token' | 'social' | 'other';
    note?: string;        // free-text, evergreen — NO volatile figures
    updatedAt?: string;   // ISO, last human review
    asserter?: string;    // 'human:<uid>' | 'model:<model-id>'
  };

  /**
   * Resolved coverage profile (WS-K closing half) — the write-back target a CONFIRMED
   * first-party coverage answer patches. When set, it overrides the derived coverage tier
   * and carries the PRESERVED attribution (asserter + source + confirmedAt) so the dossier
   * shows "verified by @x". Setting this is the conversion event (the CoverageNote flips from
   * "Limited view" to resolved). NEVER touches the editorial trajectory verdict (firewalled).
   */
  coverageProfile?: {
    tier?: 'high' | 'low' | 'na';
    note?: string;                 // evergreen free-text — what the better metric is
    source?: { url?: string; asserter?: string; confirmedAt?: string };
    updatedAt?: string;            // ISO
  };

  /**
   * AI Legibility Score (engine: docs/.../2026-06-24-ai-legibility-score-engine-design.md).
   * One rubric, three probe inputs. bare=public/hook · pointed=client · open=INTERNAL only.
   * Present only on monitored entities the engine has scored. accuracy/recency are null when N/A.
   */
  aiLegibility?: {
    bare:    AiLegibilityProbeScore;
    pointed: AiLegibilityProbeScore;
    open?:   AiLegibilityProbeScore & { citedDescout: boolean; citationUrls?: string[] };
    delta?:  { coverage: number; accuracy: number | null; recency: number | null }; // pointed − bare
    recordHash: string;
    judgeModel: string;          // score_model
    scorePromptVersion: string;  // rubric version
    probeModels: string[];
    updatedAt: string;           // ISO
  };

  /**
   * Monitoring state — the entity-graph LIFECYCLE axis ("are we observing this at all?").
   * Widened this leg from the half-built `active | dormant` to `live | dormant | archived`:
   *   - `live`     — actively observed (the default; absent/legacy `'active'` normalize here).
   *   - `dormant`  — cost-pause (reversible): SUSPENDS active spend WITHOUT deleting `linkedAsset`
   *                  (history preserved; the DYLI lesson) and WITHOUT hiding the dossier
   *                  (right-of-reply stays). Any new signal/answer flips it back to `live`.
   *   - `archived` — deliberate, TERMINAL wind-down (sunset). Reversible only by a deliberate
   *                  operator un-archive; a new signal does NOT un-archive.
   *
   * ⚠ Store the CANONICAL vocab; legacy `'active'` and ABSENT are handled by
   * `normalizeMonitoringState` on read (mirrors `entityPriority` + `normalizeEntityPriority`) —
   * so the field type does NOT admit `'active'`. NEVER hand-check literals or filter
   * `== 'live'`; the invariant everywhere is `!= 'archived'` (absent must count as live).
   *
   * MS0 scope: this is the TYPE only. The `dormancy` block below is UNCHANGED (existing writers
   * still name it; MS1 generalizes them onto `monitoring`). The `monitoring` +
   * `evidenceSnapshot` blocks are additive/optional — no reader/writer wired to them yet.
   */
  monitoringState?: MonitoringState;
  dormancy?: {
    since?: string;       // ISO — when an operator confirmed dormant
    reason?: string;      // the hedged, presume-up-respecting rationale
    questionId?: string;  // the still-OPEN "is this operating?" question (re-activation hook)
    asserter?: string;    // who confirmed dormant ('human:<uid>')
  };

  /**
   * Generalized monitoring record (widens `dormancy` beyond the dormant-only cost-governor;
   * MS1 migrates the writers). All ISO strings — NEVER Timestamps (FieldValue.serverTimestamp()
   * is illegal inside arrays; ISO is the house convention for these). Additive/optional.
   */
  monitoring?: {
    reason?: string;           // why dormant/archived (generalizes dormancy.reason)
    since?: string;            // ISO — when this state was entered
    lastMonitoredAt?: string;  // ISO — last time an agent actually looked (from twitterHandles.lastFetchedAt)
    asserter?: string;         // who/what set it ('human:0xMal_' | 'auto:retier_long_silence')
    questionId?: string;       // the "is this still operating?" re-activation hook (dormant only)
  };

  /**
   * Archived one-time coverage — the queryable, agent-readable, edit-proof receipt layer beneath
   * the human-readable `## Wind-Down` glossary prose. Structured per-source receipts (reuses the
   * `entity_relationships.sources[]` edge-source shape). Frozen at archive-time so the dossier's
   * evidentiary richness doesn't decay once spend stops. Additive/optional; all ISO strings.
   */
  evidenceSnapshot?: {
    asOf: string;              // ISO
    note?: string;             // 1–2 line summary of the wind-down
    sources: Array<{
      url: string;
      snippet?: string;        // the specific supporting line
      observedAt?: string;     // ISO — when it was viewed (first-hand research)
      asserter?: string;       // 'human:0xMal_' | 'auto:*'
    }>;
  };

  // ── WS-K L3 ownership primitive (Claim → Verify → Member) — additive, all optional ──

  /**
   * The MEMBER who has PROVEN ownership of this entity via the proof ladder (Twitter / domain /
   * on-chain). NULL/absent = unclaimed. Set ONLY by the server-side claim state machine
   * (src/lib/ownership/claim-service.ts) via the Admin SDK after a proof verifies — never a
   * client write.
   *
   * THE COI LINE: ownership grants a member RIGHT-OF-REPLY (a member's answer on THIS entity is
   * asserted `human:member:<uid>`, status FORCED `proposed`) — it NEVER grants the right to set a
   * verdict (trajectory / verifiedStatus / coverageProfile). Identity buys the right to POINT,
   * never the right to ASSERT.
   */
  ownerUid?: string;
  ownershipProof?: {
    method: 'twitter' | 'domain' | 'onchain'; // which proof-ladder rung verified ownership
    verifiedAt: string;                        // ISO — when the proof was accepted
    detail?: string;                           // the proof value (handle / domain) — NEVER secrets
  };
}

export interface AiLegibilityProbeScore {
  coverage: number;            // 0–10
  accuracy: number | null;     // 0–10, null if N/A (honest refusal)
  recency: number | null;      // 0–10, null if N/A (no recent events)
  nCorrect: number;
  nTotal: number;
  perModel?: Array<{ model: string; coverage: number; accuracy: number | null; recency: number | null; verbatim: string; finish?: string; blank?: boolean; error?: string }>;
  assessedAt: string;          // ISO
}

// Entity types for the new entity-based system
export enum EntityType {
  PERSON = 'person',
  PROJECT = 'project',
  GAME = 'game',
  // A named IP figure / persona (mascot, PFP character) whose identity is the figure
  // itself, almost always asset-backed (token or NFT). A "clearer type of project"
  // (like GAME/PROTOCOL): its own kind because it renders + relates differently —
  // carries the MASCOT_OF edge. NOT a community/chain. (2026-06-09, Lam approved.)
  CHARACTER = 'character',
  PROTOCOL = 'protocol',
  ORGANIZATION = 'organization'
}

// Entity coverage tiers (migrated 2026-06 from core/established/emerging/new).
// Drives the breaking-news rubric: newsworthiness bar + verifier trust per tier
// (see backend resolve-subject-tag.normalizeTier / content-pipeline-config).
export enum EntityPriority {
  FLAGSHIP = 'flagship',   // Top coverage — trusted, lowest newsworthiness bar
  STANDARD = 'standard',   // Default tier — normal bar, trusted
  LIGHT = 'light',         // Higher bar, not auto-trusted
  MUTED = 'muted',         // Never produces a breaking candidate
}

// Legacy value → tier, mirrors backend resolve-subject-tag.LEGACY_TIER_ALIASES so
// the admin UI shows the right tier for entities not yet reclassified by WS3.
const LEGACY_PRIORITY_ALIASES: Record<string, EntityPriority> = {
  core: EntityPriority.FLAGSHIP,
  established: EntityPriority.STANDARD,
  emerging: EntityPriority.LIGHT,
  new: EntityPriority.STANDARD,
};

/** Normalize any stored entityPriority (new vocab, legacy, or unset) to a tier. */
export function normalizeEntityPriority(
  value?: string | null
): EntityPriority | undefined {
  if (!value) return undefined;
  if ((Object.values(EntityPriority) as string[]).includes(value)) {
    return value as EntityPriority;
  }
  return LEGACY_PRIORITY_ALIASES[value];
}

// Entity links structure
export interface EntityLink {
  url: string;
  title?: string;
  type: EntityLinkType;
}

export enum EntityLinkType {
  WEBSITE = 'website',
  TWITTER = 'twitter', // Also handles X links
  GITHUB = 'github',
  DISCORD = 'discord',
  TELEGRAM = 'telegram',
  DOCS = 'docs',
  WHITEPAPER = 'whitepaper',
  // New link types
  YOUTUBE = 'youtube',
  MEDIUM = 'medium',
  PARAGRAPH = 'paragraph',
  COINGECKO = 'coingecko',
  COINMARKETCAP = 'coinmarketcap',
  DEXSCREENER = 'dexscreener',
  TIKTOK = 'tiktok',
  INSTAGRAM = 'instagram',
  GIPHY = 'giphy',
  LINKEDIN = 'linkedin',
  FACEBOOK = 'facebook',
  REDDIT = 'reddit',
  OPENSEA = 'opensea',
  BLUR = 'blur',
  MAGICEDEN = 'magiceden',
  MIRROR = 'mirror',
  SUBSTACK = 'substack',
  TWITCH = 'twitch',
  SPOTIFY = 'spotify',
  FARCASTER = 'farcaster',
  WARPCAST = 'warpcast',
  OTHER = 'other'
}

// Entity images
export interface EntityImage {
  url: string;
  caption?: string;
  isPrimary?: boolean; // Primary image shown in hero section
  order?: number; // Display order in gallery
}

// Migration status tracking
export enum MigrationStatus {
  LEGACY = 'legacy',           // Old hierarchical tag, not yet reviewed
  MIGRATING = 'migrating',     // In process of being converted
  COMPLETED = 'completed'      // Fully migrated to new system
}

// Tag associations (many-to-many, bidirectional)
export interface TagAssociation {
  id: string;
  tagId: string; // Source tag
  associatedTagId: string; // Target tag
  addedAt: Date;
  addedBy?: string; // User ID
}

// Hierarchical tag system - simplified to parent/child relationship
export enum TagType {
  PARENT = 'parent',    // Top-level categories (Blockchain, Collections, DeFi, etc.)
  CHILD = 'child'       // Specific tags under parents (Abstract, Lil Pudgys, etc.)
}

// Predefined parent categories for consistent structure
export enum ParentTagCategory {
  BLOCKCHAIN = 'blockchain',     // Abstract, Ethereum, Polygon
  COLLECTIONS = 'collections',   // Lil Pudgys, Pudgy Penguins, etc.
  DEFI = 'defi',                // Uniswap, Compound, Aave
  CONTENT_TYPE = 'content-type', // Guide, Tutorial, News, Analysis
  DIFFICULTY = 'difficulty',     // Beginner, Intermediate, Advanced
  ECOSYSTEM = 'ecosystem'        // Abstract Foundation, Community, Partners
}

// Many-to-many relationship with weights
export interface ArticleTag {
  articleId: string;
  tagId: string;
  weight: number; // Primary tag = 100, secondary = 75, tertiary = 50
  addedAt: Date;
  addedBy: string; // userId
}

// Hierarchy display helper
export interface TagHierarchyNode {
  tag: Tag;
  children: TagHierarchyNode[];
  depth: number;
}

// Form data for admin
export interface TagFormData {
  name: string;
  slug: string;
  description: string;
  label?: string; // Optional display label for parent tags
  parent: string; // Parent tag ID (empty for parent tags) - LEGACY
  type: TagType; // LEGACY
  color: string;
  isActive: boolean;

  // Chain scope — see Tag.chains. New tags should set this; default ['abstract'] if omitted.
  chains?: string[];

  // NEW: Entity fields (optional)
  entityType?: EntityType | null;
  entityPriority?: EntityPriority;
  hasGlossaryPage?: boolean;
  glossaryContent?: string;
  seoExcerpt?: string;
  links?: EntityLink[];
  images?: EntityImage[];
  linkedAsset?: {
    type: 'token' | 'nft' | 'collection';
    assetId: string;
    chain?: string;
  } | null;  // null = remove from Firestore, undefined = don't update
  associatedTags?: string[];
  twitter?: {
    handleId: string;
    username: string;
    followers: number;
    verified: boolean;
    lastSynced: Date;
    autoUpdate: boolean;
  };
  twitterHandles?: string[];
  primaryTwitterHandle?: string;
  migrationStatus?: MigrationStatus;
}

// Search and filtering
export interface TagSearchParams {
  query?: string;
  type?: TagType; // LEGACY
  parentCategory?: ParentTagCategory; // LEGACY
  isActive?: boolean;
  trending?: boolean;
  parentId?: string; // LEGACY
  limit?: number;

  // NEW: Entity filtering
  entityType?: EntityType | null;
  hasGlossaryPage?: boolean;
  migrationStatus?: MigrationStatus;
}

// Analytics event types for future implementation
export interface TagEvent {
  type: 'tag_view' | 'tag_click' | 'tag_search' | 'article_tag_click';
  tagId: string;
  tagName: string;
  articleId?: string;
  timestamp: Date;
  userId?: string;
}

// Tag page content structure
export interface TagPageData {
  tag: Tag;
  articles: string[]; // Article IDs
  relatedTags: Tag[];
  childTags: Tag[];
  parentTag?: Tag;
  aiDescription?: string;
  metaDescription?: string;
}

// Featured tags configuration for homepage
export interface FeaturedTag {
  id: string;
  tagId: string; // Reference to the parent tag
  title: string; // Display title for the featured tag
  description?: string; // Optional description
  order: number; // Display order (0 = first)
  isEnabled: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Featured tags management
export interface FeaturedTagsConfig {
  id: string;
  featuredTags: FeaturedTag[];
  lastUpdated: Date;
  updatedBy: string; // User ID who made the change
}

// Lightweight entity data for search/sidebar components
export interface EntitySearchData {
  id: string;
  name: string;
  slug: string;
  entityType: EntityType;
  twitterHandles: string[];
  primaryImage?: string;
}